import React, { useState, useEffect } from "react";
import styles from "./ActivePartnerships.module.css";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { BiLinkAlt } from "react-icons/bi";

// 平台图映射（Simple Icons极简风格）
const platformIcons = {
  tiktok:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/tiktok.svg",
  instagram:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/instagram.svg",
  youtube:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/youtube.svg",
};

const postIcon =
  "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/link.svg";

const postTemplates = [
  { title: "How to use JanneyAI for content rewriting" },
  { title: "Instagram Post Example" },
  { title: "YouTube Video Example" },
  { title: "Another TikTok Post" },
  { title: "Another YouTube Video" },
];

const PartnershipCard = ({
  avatar_url,
  name,
  username,
  followers,
  status,
  lastUpdate,
  platform,
  profile_url,
  post,
  expanded,
  onClick,
}) => {
  const normalized = (status || "").toLowerCase().replace(/_/g, " ").trim();
  return (
    <>
      <article
        className={styles.partnerCard}
        onClick={(e) => {
          if (e.target.closest(".profile-link-icon")) return;
          onClick();
        }}
        style={{ cursor: "pointer" }}
      >
        <div className={styles.partnerProfile}>
          <div style={{ position: "relative", display: "inline-block" }}>
            <img src={avatar_url} alt={name} className={styles.partnerAvatar} />
            {platform && platformIcons[platform] && (
              <img
                src={platformIcons[platform]}
                alt={platform}
                style={{
                  width: 24,
                  height: 24,
                  position: "absolute",
                  right: -6,
                  bottom: -6,
                  background: "#fff",
                  padding: 2,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                }}
              />
            )}
          </div>
          <div className={styles.partnerInfo} style={{ position: "relative" }}>
            <h3
              className={styles.partnerName}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              {name}
              <a
                href={profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="profile-link-icon"
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  marginLeft: 6,
                }}
                title="Go to profile"
              >
                <FiExternalLink
                  size={20}
                  color="#888"
                  style={{ opacity: 0.7 }}
                />
              </a>
            </h3>
            <p className={styles.partnerMeta}>
              {username} <span className={styles.separator}>|</span> {followers}{" "}
              followers
            </p>
          </div>
        </div>
        <div className={styles.partnerStatus}>
          <span className={`${styles.badge} ${styles[normalized] || ""}`}>
            {normalized}
          </span>
          <p className={styles.partnerUpdate}>{lastUpdate}</p>
        </div>
      </article>
      {expanded && post && (
        <div
          style={{
            margin: "0 0 18px 0",
            background: "#f8fafd",
            borderRadius: 12,
            padding: 18,
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            display: "flex",
            alignItems: "center",
            gap: 14,
            maxWidth: 700,
            marginLeft: 120,
          }}
        >
          <a
            href="https://www.tiktok.com/@signbridgeai/video/7497393951884741910"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "inline-block" }}
          >
            <BiLinkAlt
              size={28}
              color="#888"
              style={{ opacity: 0.7, verticalAlign: "middle" }}
            />
          </a>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, fontSize: 16 }}>{post.title}</div>
            <div style={{ color: "#888", fontSize: 14, marginTop: 2 }}>
              Published: {post.date}
            </div>
          </div>
          <div style={{ textAlign: "right", minWidth: 120 }}>
            <div style={{ fontSize: 15, color: "#222" }}>
              Views: <b>{post.views}</b>
            </div>
            <div style={{ fontSize: 15, color: "#e57373", marginTop: 2 }}>
              ❤ {post.likes} &nbsp; 💬 {post.comments}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Partnerships = ({ campaignId }) => {
  const [partners, setPartners] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!campaignId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const axios = (await import("axios")).default;
        // 1. 获取campaign-influencer列表
        const ciRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/campaign-influencer?campaign_id=${campaignId}`,
        );
        const ciList = ciRes.data?.campaign_influencers || [];
        
        // 2. 并行获取每个influencer的详细信息和post
        const influencerPromises = ciList.map(async (ci, index) => {
          try {
            // 并行获取influencer信息和post数据
            const [infRes, postRes] = await Promise.all([
              axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/influencer?influencer_id=${ci.influencer_id}`
              ),
              axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/post?campaign_influencer_id=${ci.campaign_influencer_id}`
              )
            ]);
            
            const inf = infRes.data?.influencer || {};
            const apiPosts = postRes.data?.posts || [];
            
            console.log('Post data for influencer:', inf.display_name, apiPosts);
            
            // 获取influencer extras（followers数）
            let followers = "N/A";
            try {
              const extrasRes = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/influencer-extras?influencer_id=${ci.influencer_id}`
              );
              followers = extrasRes.data?.extras_json?.follower_count?.toLocaleString?.() || "N/A";
            } catch (e) {
              followers = "N/A";
            }
            
            // 处理日期时区转换
            let date = ci.updated_at || "";
            if (date) {
              try {
                // 直接使用 Date 构造函数解析 HTTP 日期格式
                const localDate = new Date(date);
                // 检查日期是否有效
                if (isNaN(localDate.getTime())) {
                  console.error('Invalid date:', date);
                  date = "Invalid Date";
                } else {
                  // 格式化为 YYYY-MM-DD HH:mm 格式
                  date = localDate.toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }).replace(',', '');
                }
              } catch (e) {
                console.error('Date conversion error:', e);
                date = "Invalid Date";
              }
            }
            
            return {
              avatar_url: inf.avatar_url,
              profile_url: inf.profile_url,
              name: inf.display_name,
              username: inf.handle,
              followers,
              status: ci.status || "connected",
              lastUpdate: date,
              platform: inf.platform,
              posts: apiPosts.length > 0 ? apiPosts.map((p) => {
                // 处理日期时区转换
                let date = p.published_at || p.created_at || "";
                if (date) {
                  try {
                    // 直接使用 Date 构造函数解析 HTTP 日期格式
                    const localDate = new Date(date);
                    // 检查日期是否有效
                    if (isNaN(localDate.getTime())) {
                      console.error('Invalid date:', date);
                      date = "Invalid Date";
                    } else {
                      // 格式化为 YYYY-MM-DD HH:mm 格式
                      date = localDate.toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      }).replace(',', '');
                    }
                  } catch (e) {
                    console.error('Date conversion error:', e);
                    date = "Invalid Date";
                  }
                }
                
                return {
                  url: p.url,
                  title: p.title,
                  date: date,
                  views: p.views,
                  likes: p.likes,
                  comments: p.comments,
                };
              }) : [{
                ...postTemplates[index],
                url: "",
                date: "",
                views: 0,
                likes: 0,
                comments: 0,
              }],
            };
          } catch (e) {
            return null;
          }
        });
        
        const influencerDetails = await Promise.all(influencerPromises);
        setPartners(influencerDetails.filter(Boolean));
      } catch (e) {
        setError("Failed to load partnerships data");
        setPartners([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [campaignId]);

  return (
    <section className={styles.partnerships}>
      <header className={styles.partnershipsTitle}>Active Partnerships</header>
      {loading ? (
        <div
          style={{
            padding: 60,
            textAlign: "center",
            color: "#1976d2",
            fontSize: 20,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px"
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e3f2fd",
              borderTop: "4px solid #1976d2",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }}
          />
          <div>Loading Partnerships Data...</div>
        </div>
      ) : error ? (
        <div
          style={{
            padding: 60,
            textAlign: "center",
            color: "#e53e3e",
            fontSize: 20,
            width: "100%",
          }}
        >
          {error}
        </div>
      ) : partners.length === 0 ? (
        <div
          style={{
            padding: 60,
            textAlign: "center",
            color: "#888",
            fontSize: 20,
            width: "100%",
          }}
        >
          No active partnerships for this campaign.
        </div>
      ) : (
        <div className={styles.partnersList}>
          {partners.map((partner, index) => (
            <React.Fragment key={index}>
              <PartnershipCard
                {...partner}
                post={undefined}
                expanded={index === expandedIndex}
                onClick={() =>
                  setExpandedIndex(index === expandedIndex ? -1 : index)
                }
              />
              {index === expandedIndex &&
                partner.posts &&
                partner.posts.length > 0 &&
                partner.posts.map((post, idx) => (
                  <div
                    key={idx}
                    style={{
                      margin: "0 0 18px 0",
                      background: "#f8fafd",
                      borderRadius: 12,
                      padding: 18,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 24,
                      width: "100%",
                      marginLeft: 0,
                      flexWrap: "wrap",
                    }}
                  >
                    <a
                      href={post.url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "inline-block", marginTop: 4 }}
                    >
                      <BiLinkAlt
                        size={28}
                        color="#888"
                        style={{ opacity: 0.7, verticalAlign: "middle" }}
                      />
                    </a>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          fontSize: 18,
                          wordBreak: "break-all",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden"
                        }}
                      >
                        {post.title &&
                          post.title.replace(/#[\w\d_\u4e00-\u9fa5]+/g, "").trim()}
                      </div>
                      {post.title &&
                        post.title.match(/#[\w\d_\u4e00-\u9fa5]+/g) && (
                          <div
                            style={{
                              margin: "6px 0 0 0",
                              fontSize: 15,
                              color: "#1976d2",
                              fontWeight: 600,
                              wordBreak: "break-all",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            {post.title.match(/#[\w\d_\u4e00-\u9fa5]+/g).map((tag, i) => (
                              <span
                                key={i}
                                style={{
                                  background: "#eaf2ff",
                                  borderRadius: 6,
                                  padding: "2px 8px",
                                  marginRight: 4,
                                }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      <div style={{ color: "#888", fontSize: 14, marginTop: 6 }}>
                        Published: {post.date}
                      </div>
                    </div>
                    <div
                      style={{
                        textAlign: "right",
                        minWidth: 120,
                        marginLeft: "auto",
                      }}
                    >
                      <div style={{ fontSize: 15, color: "#222" }}>
                        Views: <b>{post.views}</b>
                      </div>
                      <div style={{ fontSize: 15, color: "#e57373", marginTop: 2 }}>
                        ❤ {post.likes} &nbsp; 💬 {post.comments}
                      </div>
                    </div>
                  </div>
                ))}
            </React.Fragment>
          ))}
        </div>
      )}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </section>
  );
};

export default Partnerships;
